/**
 * Auditoria global — panel profesional de seguimiento y supervision.
 *
 * Vista cross-tenant para SUPER_ADMIN: KPIs de seguridad, filtros
 * avanzados, tabla con detalle expandible y exportacion CSV.
 */

import { useState, useMemo } from 'react'
import {
  Shield,
  Loader2,
  Download,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Activity,
  Users,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  useAuditSearch,
  useSecurityStats,
} from '@/application/admin-audit/useAdminAudit'
import { exportAuditCsv } from '@/application/admin-audit/admin-audit.service'
import type {
  AuditSearchFilters,
  AuditLogEntry,
} from '@/application/admin-audit/admin-audit.service'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'

const ACTION_OPTIONS = [
  'CREATE',
  'READ',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'LOGIN_FAILED',
  'ACCESS_DENIED',
  'DOWNLOAD',
  'UPLOAD',
  'EXPORT',
  'ASSIGN',
  'RETURN',
  'SIGN',
  'AMEND',
]

const RESOURCE_OPTIONS = [
  'user',
  'patient',
  'study',
  'report',
  'role',
  'permission',
  'system',
  'auth',
  'worklist',
  'institution',
  'plan',
  'subscription',
  'payment',
]

const PAGE_SIZE = 50

export default function AuditGlobalPage() {
  const [filters, setFilters] = useState<AuditSearchFilters>({
    limit: PAGE_SIZE,
    offset: 0,
  })
  const [showFilters, setShowFilters] = useState(false)

  const statsQuery = useSecurityStats()
  const searchQuery = useAuditSearch(filters)

  const stats = statsQuery.data
  const result = searchQuery.data

  const updateFilter = <K extends keyof AuditSearchFilters>(
    key: K,
    value: AuditSearchFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value, offset: 0 }))
  }

  const clearFilters = () => {
    setFilters({ limit: PAGE_SIZE, offset: 0 })
  }

  const handleExport = async () => {
    try {
      const blob = await exportAuditCsv(filters)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success('Export descargado')
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }

  const totalPages = result ? Math.ceil(result.total / PAGE_SIZE) : 0
  const currentPage = Math.floor((filters.offset ?? 0) / PAGE_SIZE) + 1

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Auditoria y supervision
          </h1>
          <p className="text-[var(--foreground-muted)]">
            Panel profesional de seguimiento de eventos cross-tenant. Compliance
            Resolucion 2003 y Ley 1581.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => statsQuery.refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refrescar
          </Button>
          <Button onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* KPIs de seguridad */}
      {statsQuery.isLoading ? (
        <div className="flex justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={<Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
              label="Eventos hoy"
              value={stats.eventsToday.toLocaleString('es-CO')}
              hint={`${stats.events24h.toLocaleString('es-CO')} en 24h`}
            />
            <KpiCard
              icon={<XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
              label="Login fallidos (24h)"
              value={stats.failedLogins24h.toLocaleString('es-CO')}
              hint="Posibles intentos de fuerza bruta"
              warn={stats.failedLogins24h > 10}
            />
            <KpiCard
              icon={<AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />}
              label="Accesos denegados (24h)"
              value={stats.accessDenied24h.toLocaleString('es-CO')}
              hint="Intentos sin permiso"
              warn={stats.accessDenied24h > 5}
            />
            <KpiCard
              icon={<CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />}
              label="Tasa de exito (7d)"
              value={`${stats.successRate}%`}
              hint={`${stats.events7days.toLocaleString('es-CO')} eventos totales`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Distribucion (7 dias)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <StatusBar
                  label="Exitosos"
                  count={stats.eventsByStatus.SUCCESS}
                  total={stats.events7days}
                  color="bg-green-500"
                />
                <StatusBar
                  label="Fallidos"
                  count={stats.eventsByStatus.FAILURE}
                  total={stats.events7days}
                  color="bg-red-500"
                />
                <StatusBar
                  label="Denegados"
                  count={stats.eventsByStatus.DENIED}
                  total={stats.events7days}
                  color="bg-yellow-500"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Top usuarios (7 dias)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.topUsers.length === 0 ? (
                  <p className="text-xs text-[var(--foreground-muted)]">Sin datos.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {stats.topUsers.map((u, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{u.userEmail ?? 'Anonimo'}</span>
                        <span className="font-medium text-[var(--foreground-muted)]">
                          {u.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Top acciones (7 dias)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.topActions.length === 0 ? (
                  <p className="text-xs text-[var(--foreground-muted)]">Sin datos.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {stats.topActions.map((a, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="font-mono text-xs">{a.action}</span>
                        <span className="font-medium text-[var(--foreground-muted)]">
                          {a.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Actividad ultimas 24h</CardTitle>
            </CardHeader>
            <CardContent>
              <HourlyActivityBar events={stats.eventsByHour} />
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Filtros */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtros de busqueda
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setShowFilters((v) => !v)}>
            {showFilters ? 'Ocultar' : 'Mostrar'}
            <ChevronDown
              className={`h-4 w-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </Button>
        </CardHeader>
        {showFilters && (
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FilterField label="ID del usuario">
                <Input
                  value={filters.userId ?? ''}
                  onChange={(e) => updateFilter('userId', e.target.value || undefined)}
                  placeholder="UUID del usuario"
                />
              </FilterField>
              <FilterField label="Accion">
                <select
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
                  value={filters.action ?? ''}
                  onChange={(e) => updateFilter('action', e.target.value || undefined)}
                >
                  <option value="">Todas</option>
                  {ACTION_OPTIONS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Tipo de recurso">
                <select
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
                  value={filters.resourceType ?? ''}
                  onChange={(e) =>
                    updateFilter('resourceType', e.target.value || undefined)
                  }
                >
                  <option value="">Todos</option>
                  {RESOURCE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Estado">
                <select
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
                  value={filters.status ?? ''}
                  onChange={(e) =>
                    updateFilter(
                      'status',
                      (e.target.value as 'SUCCESS' | 'FAILURE' | 'DENIED' | '') || undefined
                    )
                  }
                >
                  <option value="">Todos</option>
                  <option value="SUCCESS">Exitoso</option>
                  <option value="FAILURE">Fallido</option>
                  <option value="DENIED">Denegado</option>
                </select>
              </FilterField>
              <FilterField label="IP">
                <Input
                  value={filters.ipAddress ?? ''}
                  onChange={(e) =>
                    updateFilter('ipAddress', e.target.value || undefined)
                  }
                  placeholder="Ej. 192.168.1.10"
                />
              </FilterField>
              <FilterField label="Recurso ID">
                <Input
                  value={filters.resourceId ?? ''}
                  onChange={(e) =>
                    updateFilter('resourceId', e.target.value || undefined)
                  }
                  placeholder="UUID del recurso"
                />
              </FilterField>
              <FilterField label="Desde">
                <Input
                  type="datetime-local"
                  value={filters.dateFrom ?? ''}
                  onChange={(e) =>
                    updateFilter('dateFrom', e.target.value || undefined)
                  }
                />
              </FilterField>
              <FilterField label="Hasta">
                <Input
                  type="datetime-local"
                  value={filters.dateTo ?? ''}
                  onChange={(e) =>
                    updateFilter('dateTo', e.target.value || undefined)
                  }
                />
              </FilterField>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Limpiar
              </Button>
              <Button size="sm" onClick={() => searchQuery.refetch()}>
                <Search className="h-4 w-4 mr-2" />
                Aplicar
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Tabla de logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            Eventos {result && `(${result.total.toLocaleString('es-CO')})`}
          </CardTitle>
          {result && totalPages > 1 && (
            <div className="text-sm text-[var(--foreground-muted)]">
              Pagina {currentPage} de {totalPages}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {searchQuery.isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : !result || result.logs.length === 0 ? (
            <p className="p-6 text-center text-[var(--foreground-muted)]">
              Sin eventos que coincidan con los filtros.
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Accion</TableHead>
                    <TableHead>Recurso</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.logs.map((log) => (
                    <ExpandableRow key={log.id} log={log} />
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-[var(--border)]">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setFilters({
                        ...filters,
                        offset: Math.max(0, (filters.offset ?? 0) - PAGE_SIZE),
                      })
                    }
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-[var(--foreground-muted)]">
                    Mostrando {(filters.offset ?? 0) + 1} -{' '}
                    {Math.min((filters.offset ?? 0) + PAGE_SIZE, result.total)} de{' '}
                    {result.total.toLocaleString('es-CO')}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setFilters({ ...filters, offset: (filters.offset ?? 0) + PAGE_SIZE })
                    }
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// Subcomponentes
// =============================================================================

function KpiCard({
  icon,
  label,
  value,
  hint,
  warn,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
  hint?: string
  warn?: boolean
}) {
  return (
    <Card className={warn ? 'border-yellow-300' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-[var(--foreground-muted)]">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          {icon}
          <span className="text-2xl font-bold">{value}</span>
        </div>
        {hint && <p className="text-xs text-[var(--foreground-muted)] mt-1">{hint}</p>}
      </CardContent>
    </Card>
  )
}

function StatusBar({
  label,
  count,
  total,
  color,
}: {
  label: string
  count: number
  total: number
  color: string
}) {
  const pct = total === 0 ? 0 : (count / total) * 100
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="font-medium">
          {count.toLocaleString('es-CO')} ({pct.toFixed(1)}%)
        </span>
      </div>
      <div className="h-2 rounded-full bg-[var(--border)] overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function HourlyActivityBar({ events }: { events: Array<{ hour: string; count: number }> }) {
  const maxCount = useMemo(
    () => events.reduce((max, e) => Math.max(max, e.count), 0),
    [events]
  )
  if (events.length === 0) {
    return (
      <p className="text-xs text-[var(--foreground-muted)]">
        Sin actividad en las ultimas 24h.
      </p>
    )
  }
  return (
    <div className="flex items-end gap-1 h-20">
      {events.map((e, i) => {
        const height = maxCount === 0 ? 0 : (e.count / maxCount) * 100
        const time = new Date(e.hour).toLocaleTimeString('es-CO', {
          hour: '2-digit',
          minute: '2-digit',
        })
        return (
          <div
            key={i}
            className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors min-w-[3px]"
            style={{ height: `${Math.max(height, 2)}%` }}
            title={`${time}: ${e.count} eventos`}
          />
        )
      })}
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-[var(--foreground-muted)] block mb-1">
        {label}
      </label>
      {children}
    </div>
  )
}

function ExpandableRow({ log }: { log: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false)
  const rowClass =
    log.status === 'FAILURE'
      ? 'bg-red-50 dark:bg-red-950/40/50 hover:bg-red-50 dark:bg-red-950/40'
      : log.status === 'DENIED'
        ? 'bg-yellow-50 dark:bg-yellow-950/40/50 hover:bg-yellow-50 dark:bg-yellow-950/40'
        : ''

  return (
    <>
      <TableRow className={rowClass}>
        <TableCell>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="hover:text-[var(--primary)]"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </TableCell>
        <TableCell className="text-xs text-[var(--foreground-muted)]">
          {new Date(log.createdAt).toLocaleString('es-CO')}
        </TableCell>
        <TableCell>
          <span className="font-mono text-xs font-medium">{log.action}</span>
        </TableCell>
        <TableCell>
          <div>{log.resourceType}</div>
          {log.resourceName && (
            <div className="text-xs text-[var(--foreground-muted)] truncate max-w-xs">
              {log.resourceName}
            </div>
          )}
        </TableCell>
        <TableCell>
          <div className="text-sm">{log.userEmail ?? 'sistema'}</div>
          {log.userRole && (
            <div className="text-xs text-[var(--foreground-muted)]">{log.userRole}</div>
          )}
        </TableCell>
        <TableCell>
          <StatusBadge status={log.status} />
        </TableCell>
        <TableCell className="text-xs font-mono">{log.ipAddress ?? '—'}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow className={rowClass}>
          <TableCell colSpan={7} className="bg-[var(--background-muted)]">
            <div className="p-4 text-xs space-y-2">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <DetailField label="Request ID" value={log.requestId} mono />
                <DetailField label="Metodo" value={log.requestMethod} />
                <DetailField label="Path" value={log.requestPath} mono />
                <DetailField label="Resource ID" value={log.resourceId} mono />
                <DetailField label="User ID" value={log.userId} mono />
                <DetailField label="User Agent" value={log.userAgent} />
              </div>
              {log.errorMessage && (
                <div className="rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 p-2">
                  <strong>Error:</strong> {log.errorMessage}
                </div>
              )}
              {log.details && (
                <div>
                  <strong>Detalles:</strong>
                  <pre className="bg-white p-2 rounded mt-1 overflow-x-auto text-[11px]">
                    {JSON.stringify(log.details, null, 2)}
                  </pre>
                </div>
              )}
              {log.oldValues && (
                <div>
                  <strong>Valores anteriores:</strong>
                  <pre className="bg-white p-2 rounded mt-1 overflow-x-auto text-[11px]">
                    {JSON.stringify(log.oldValues, null, 2)}
                  </pre>
                </div>
              )}
              {log.newValues && (
                <div>
                  <strong>Valores nuevos:</strong>
                  <pre className="bg-white p-2 rounded mt-1 overflow-x-auto text-[11px]">
                    {JSON.stringify(log.newValues, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function StatusBadge({ status }: { status: 'SUCCESS' | 'FAILURE' | 'DENIED' }) {
  const cls =
    status === 'SUCCESS'
      ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
      : status === 'FAILURE'
        ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
        : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
  const label =
    status === 'SUCCESS' ? 'Exitoso' : status === 'FAILURE' ? 'Fallido' : 'Denegado'
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  )
}

function DetailField({
  label,
  value,
  mono,
}: {
  label: string
  value: string | null
  mono?: boolean
}) {
  return (
    <div>
      <span className="text-[var(--foreground-muted)]">{label}</span>
      <div className={mono ? 'font-mono break-all' : ''}>{value ?? '—'}</div>
    </div>
  )
}
